/* eslint-disable warp-drive/no-legacy-request-patterns */

// loadRelated loads the named async relationships of a record and returns
// every related record in one array. A relationship that fails to load (for
// example, one pointing at a record the server has already deleted) adds
// nothing, rather than blocking the caller.
export async function loadRelated(record, keys) {
  const results = await Promise.allSettled(keys.map((key) => record[key]));
  return results.flatMap((result) =>
    result.status === 'fulfilled' && result.value ? result.value.slice() : [],
  );
}

// destroyAndSaveRelated deletes a record, then saves the records that
// referenced it, so the server drops the reference to the deleted record too.
//
// Gather `related` BEFORE calling this. Once the delete commits, WarpDrive
// unloads the record and its relationships read as null. It also removes the
// record from each related record's side of the relationship (an explicit
// inverse or an `inverse: null` hasMany alike), and that is what the saves
// send. Records fetched after the delete come back from the server with the
// stale id still in them.
//
// Returns { deleted, error }:
// - deleted false: the delete failed and the record was rolled back so it
//   can be used (or deleted) again.
// - deleted true, error set: the record is gone but a related save failed.
export async function destroyAndSaveRelated(record, related = []) {
  try {
    await record.destroyRecord();
  } catch (error) {
    // A failed delete leaves the record's relationships alone; this only
    // clears its deleted flag.
    record.rollbackAttributes();
    return { deleted: false, error };
  }

  try {
    await Promise.all([...new Set(related)].map((r) => r.save()));
  } catch (error) {
    return { deleted: true, error };
  }
  return { deleted: true, error: null };
}
